import {
  buildCatalogMilestoneActivitiesForApi,
  buildCatalogMilestoneParameterUpdatesForApi,
  isCatalogMilestoneKey,
  normalizeCatalogMilestone,
  normalizeCatalogMilestonesList,
} from "./catalogMilestones";
import {
  isFollowUpMilestoneKey,
  normalizeM1Inputs,
  normalizeM2Inputs,
  normalizeM3Inputs,
  normalizeFollowUpInputs,
  normalizeRemanufacturingMilestonesList,
  syncFollowUpMilestones,
} from "./remanufacturingMilestones";
import { usesCatalogMilestones } from "../serviceOrderWorkflow";

/**
 * Canonical UI/API milestone list from an order record.
 * @param {{ serviceMilestones?: { milestones?: unknown[] }, service_milestones?: { milestones?: unknown[] } } | null | undefined} order
 * @returns {import("./remanufacturingMilestones").ServiceMilestone[]}
 */
export function milestonesFromOrder(order) {
  const raw =
    order?.serviceMilestones?.milestones ??
    order?.service_milestones?.milestones ??
    [];
  const rows = Array.isArray(raw) ? raw : [];

  if (usesCatalogMilestones(order)) {
    if (rows.length === 0) return [];
    if (rows.some((row) => isCatalogMilestoneKey(/** @type {{ key?: string }} */ (row)?.key))) {
      return normalizeCatalogMilestonesList(rows);
    }
    return [];
  }

  return normalizeRemanufacturingMilestonesList(rows);
}

/**
 * Payload for PUT /marketplace-orders/{id}/milestones
 * @param {import("./remanufacturingMilestones").ServiceMilestone[]} milestones
 * @returns {import("./remanufacturingMilestones").ServiceMilestone[]}
 */
export function toApiMilestonesPayload(milestones) {
  const hasCatalogMilestones = milestones.some((milestone) =>
    isCatalogMilestoneKey(milestone.key)
  );
  const rows = hasCatalogMilestones
    ? milestones.filter((milestone) => isCatalogMilestoneKey(milestone.key))
    : milestones;

  return syncFollowUpMilestones(rows).map((milestone) => {
    const base = {
      id: milestone.id,
      key: milestone.key,
      title: milestone.title,
      status: milestone.status,
      sortOrder: milestone.sortOrder,
    };

    if (milestone.key === "M1") {
      const inputs = normalizeM1Inputs(milestone.inputs);
      const findings = inputs.components.map((component) => ({
        id: component.id,
        gearUnitSerialNumber: component.serialNumber,
        dimensionalMeasurements: inputs.dimensionalMeasurements,
        ndtReports: inputs.ndtReports,
        visualInspectionImages: inputs.visualInspectionImages,
      }));
      return {
        ...base,
        inputs: {
          components: inputs.components.map((component) => ({
            id: component.id,
            serialNumber: component.serialNumber,
          })),
          // Backward compatibility for backend deployments that still validate
          // the original v1 M1 findings shape.
          findings,
          dimensionalMeasurements: inputs.dimensionalMeasurements,
          ndtReports: inputs.ndtReports,
          visualInspectionImages: inputs.visualInspectionImages,
        },
      };
    }

    if (milestone.key === "M2") {
      const inputs = normalizeM2Inputs(milestone.inputs);
      return {
        ...base,
        inputs: {
          postRepairMeasurements: inputs.postRepairMeasurements,
          heatTreatmentReferences: inputs.heatTreatmentReferences,
          processConfirmation: inputs.processConfirmation,
        },
      };
    }

    if (milestone.key === "M3") {
      const inputs = normalizeM3Inputs(milestone.inputs);
      return {
        ...base,
        inputs: {
          finalInspectionReport: inputs.finalInspectionReport,
          serviceCompletionConfirmed: inputs.serviceCompletionConfirmed,
        },
      };
    }

    if (isFollowUpMilestoneKey(milestone.key)) {
      const inputs = normalizeFollowUpInputs(milestone.inputs);
      return {
        ...base,
        inputs: {
          serviceCompletionConfirmed: inputs.serviceCompletionConfirmed,
          customSections: inputs.customSections.map((section) => ({
            id: section.id,
            label: section.label,
            evidence: section.evidence,
          })),
        },
      };
    }

    if (isCatalogMilestoneKey(milestone.key)) {
      const catalogMilestone = normalizeCatalogMilestone(milestone);
      const parameterUpdates =
        buildCatalogMilestoneParameterUpdatesForApi(catalogMilestone);
      return {
        ...base,
        inputs: {
          ...catalogMilestone.inputs,
          activities: buildCatalogMilestoneActivitiesForApi(catalogMilestone),
          ...(parameterUpdates.length > 0 ? { parameterUpdates } : {}),
        },
      };
    }

    return base;
  });
}

/**
 * @param {{ serviceMilestones?: { milestones?: unknown[] } } | null | undefined} order
 * @returns {{ milestones: import("./remanufacturingMilestones").ServiceMilestone[] }}
 */
export function fromApiOrder(order) {
  return { milestones: milestonesFromOrder(order) };
}
